import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../environment/environment';

export interface RequestParams {
  page: number;
  sort: string;
  order: string;
  limit: number;
}

export interface RequestAttribute {
  param: string;
  value: string;
}

@Injectable({
  providedIn: 'root'
})
export class HttpService {
  private readonly apiUrl: string = environment.apiUrl;

  constructor(private readonly http: HttpClient) { }

  genericGet<T>(endpoint: string, id?: string): Observable<T> {
    const url = id ? `${this.apiUrl}/${endpoint}/${id}` : `${this.apiUrl}/${endpoint}`;
    return this.http.get<T>(url)
      .pipe(map((response: T) => response));
  }

  genericGetDownload<T>(endpoint: string, id?: string): Observable<T> {
    const url = id ? `${this.apiUrl}/${endpoint}/${id}` : `${this.apiUrl}/${endpoint}`;
    return this.http.get(url, {responseType: 'blob'})
      .pipe(map((response: any) => response));
  }

  genericGetParams<T>(endpoint: string, attribute: RequestAttribute[]): Observable<T> {
    const url = `${this.apiUrl}/${endpoint}`;
    let params = new HttpParams();
    if (!!attribute) {
      attribute.forEach((att) => params = params.append(att.param, att.value));
    }
    return this.http.get<T>(url, { params })
      .pipe(map((response: T) => {
        return response;
      }));
  }

  genericPost<T>(endpoint: string, data?: unknown): Observable<T> {
    let body = new HttpParams();
    if(data) {
        Object.entries(data).forEach(([key, value]) => {
            body = body.set(key, value as string);
        });
    }
    const url = `${this.apiUrl}/${endpoint}`;
    return this.http.post<T>(url, body).pipe(map((response: T) => response));
  }

  genericPostJson<T>(endpoint: string, body: unknown, id?: string): Observable<T> {
    const url = id ? `${this.apiUrl}/${endpoint}/${id}` : `${this.apiUrl}/${endpoint}`;
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post<T>(url, body, { headers }).pipe(map((response: T) => response));
  }

  genericPostParams<T>(endpoint: string, requestParams: RequestParams, attribute?: RequestAttribute[], data?: any): Observable<T> {
    let params = new HttpParams()
      .set('page', requestParams.page.toString())
      .set('sort', requestParams.sort)
      .set('order', requestParams.order)
      .set('limit', requestParams.limit.toString());
    if (!!attribute) {
      attribute.forEach((att) => params = params.set(att.param, att.value));
    }
    const url = `${this.apiUrl}/${endpoint}`;
    return this.http.post<T>(url, data, { params })
      .pipe(map((response: T) => response));
  }

  genericPostDownload<T>(endpoint: string, body: any, attribute?: RequestAttribute[]): Observable<T> {
    const url = `${this.apiUrl}/${endpoint}`;
    let params = new HttpParams();

    if (!!attribute) {
      attribute.forEach((att) => params = params.append(att.param, att.value));
    }
    return this.http.post(url, body, {responseType:'arraybuffer'})
      .pipe((response: any) => {
        return response;
      });
  }

  genericPut<T>(endpoint: string, data: any, id?: string): Observable<T> {
    const url = id ? `${this.apiUrl}/${endpoint}/${id}` : `${this.apiUrl}/${endpoint}`

    return this.http.put<T>(url, data)
      .pipe(map((response: T) => response));
  }

  genericPatch<T>(endpoint: string, data: any, id?: string): Observable<T> {
    const url = id ? `${this.apiUrl}/${endpoint}/${id}` : `${this.apiUrl}/${endpoint}`;

    return this.http.patch<T>(url, data)
      .pipe(map((response: T) => response));
  }

  genericDelete<T>(endpoint: string, id?: string): Observable<T> {
    const url = id ? `${this.apiUrl}/${endpoint}/${id}` : `${this.apiUrl}/${endpoint}`;
    return this.http.delete<T>(url)
      .pipe(map((response: T) => response));
  }

  genericDeleteBody<T>(endpoint: string, id: string, data: any): Observable<T> {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      body:data
    };

    const url = `${this.apiUrl}/${endpoint}/${id}`;
    return this.http.delete<T>(url, options)
      .pipe(map((response: T) => {
        return response;
      }));
  }

  genericDeleteBodyNoId<T>(endpoint: string, data: any): Observable<T> {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      body:data
    };

    const url = `${this.apiUrl}/${endpoint}`;
    return this.http.delete<T>(url, options)
      .pipe(map((response: T) => {
        return response;
      }));
  }

  genericDeleteAll<T>(endpoint: string, id: string, attribute?: RequestAttribute[]): Observable<T> {
    const url = `${this.apiUrl}/${endpoint}/${id}`;
    let params = new HttpParams();
    if (!!attribute) {
      attribute.forEach((att) => params = params.append(att.param, att.value));
    }
    return this.http.delete<T>(url, { params })
      .pipe(map((response: T) => {
        return response;
      }));
  }

  genericPutBody<T>(endpoint: string, body: any, attribute?: RequestAttribute[]): Observable<T> {
    const url = `${this.apiUrl}/${endpoint}`;
    let params = new HttpParams()
    if (!!attribute) {
      attribute.forEach((att) => params = params.append(att.param, att.value));
    }
    return this.http.put<T>(url, body,{params})
      .pipe(map((response: T) => {
        return response;
      }));
  }

}
